/**
 * v4.2.1 — Guided Action framework (frontend).
 *
 * A reusable contract for navigating from any insight surface
 * (Analytics, future AI summaries, future anomaly feeds) to a
 * destination page in a *pre-filled but never auto-mutating* state.
 *
 * Constitutional commitments:
 *   * The framework only sets URL query state. It NEVER auto-applies,
 *     auto-saves, or auto-mutates. The destination page may pre-fill,
 *     pre-select, or pre-open dialogs — but operator confirmation
 *     remains mandatory.
 *   * `evidence[]` and `explanation` are surfaced verbatim on the
 *     destination so explainability never regresses.
 *   * URL-based deep links: reload-safe, shareable internally,
 *     replay-compatible, future-audit-friendly.
 *
 * Wire format (backend → frontend):
 *
 *   GuidedAction = {
 *     kind: string         // stable machine key, drives i18n + handler
 *     label: string        // CTA copy
 *     route: string        // absolute frontend path
 *     params: object       // string-string pre-fill state
 *     evidence: string[]   // plain-language bullets
 *     explanation: string  // single sentence rendered on arrival
 *   }
 */
import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Compose an `href` from a GuidedAction. Always appends the
 * `guided_from=analytics` marker so the destination page can render
 * the Guided Arrival banner.
 */
export function guidedActionToHref(action) {
    if (!action || !action.route) return null;
    const params = new URLSearchParams();
    Object.entries(action.params || {}).forEach(([k, v]) => {
        if (v === undefined || v === null || v === "") return;
        params.append(k, String(v));
    });
    if (!params.has("guided_from")) params.append("guided_from", "analytics");
    if (!params.has("guided_kind") && action.kind) {
        params.append("guided_kind", action.kind);
    }
    const qs = params.toString();
    return qs ? `${action.route}?${qs}` : action.route;
}

/**
 * Read the GuidedAction state out of the current URL search params.
 *
 * Returns a stable object the destination page can consume:
 *   {
 *     isGuided: boolean,         // true if `guided_from` is present
 *     source: string,            // e.g. ?guided_from=analytics
 *     kind: string,              // ?guided_kind=...
 *     params: { ...everything else from the query string... }
 *   }
 */
export function useGuidedActionParams() {
    const location = useLocation();
    return useMemo(() => {
        const sp = new URLSearchParams(location.search || "");
        const params = {};
        for (const [k, v] of sp.entries()) params[k] = v;
        return {
            isGuided: !!params.guided_from,
            source: params.guided_from || "",
            kind: params.guided_kind || "",
            params,
        };
    }, [location.search]);
}

/**
 * Clear the guided arrival markers from the URL without unmounting the
 * page. Called by the banner when the operator dismisses it — the
 * pre-fill state is preserved (we only strip `guided_from`/`guided_kind`).
 */
export function useClearGuidedArrival() {
    const location = useLocation();
    const navigate = useNavigate();
    return () => {
        const sp = new URLSearchParams(location.search || "");
        sp.delete("guided_from");
        sp.delete("guided_kind");
        navigate(
            { pathname: location.pathname, search: sp.toString() ? `?${sp.toString()}` : "" },
            { replace: true },
        );
    };
}

/**
 * Convenience hook for destination pages: fires `onArrived(params)`
 * exactly once when the user lands with `guided_from=...` in the URL.
 *
 * Use this to pre-open a dialog, focus a row, or scroll into view.
 * The hook is idempotent — re-renders do not re-fire it.
 */
export function useOnGuidedArrival(onArrived) {
    const { isGuided, params } = useGuidedActionParams();
    useEffect(() => {
        if (!isGuided) return;
        if (typeof onArrived === "function") onArrived(params);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isGuided]);
}
